import type { BadgeProps } from '@/components/ui/Badge'
import i18n, { intlLocale } from '@/i18n'
import { formatDate } from '@/utils/format'

export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'
export type LeaveKind = 'staff' | 'student'
export type LeaveAppliesTo = 'staff' | 'student' | 'both'
export type LeaveColour = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'

export const LEAVE_STATUSES: LeaveStatus[] = ['pending', 'approved', 'rejected', 'cancelled']
export const LEAVE_APPLIES_TO: LeaveAppliesTo[] = ['staff', 'student', 'both']
export const LEAVE_COLOURS: LeaveColour[] = ['brand', 'success', 'warning', 'danger', 'info', 'neutral']

export interface LeaveType {
  id: number
  name: string
  appliesTo: LeaveAppliesTo
  maxDaysPerYear: number | null
  requiresAttachment: boolean
  isPaid: boolean | null
  colour: LeaveColour
  active: boolean
  requestCount: number | null
}

export interface LeaveBalance {
  typeId: number
  typeName: string
  colour: LeaveColour
  allowance: number | null
  used: number
  pending: number
  remaining: number | null
}

export interface LeaveBalanceImpact {
  allowance: number | null
  used: number
  pending: number
  remaining: number | null
  afterApproval: number | null
}

export interface LeaveRequest {
  id: number
  type: { id: number; name: string; colour: LeaveColour }
  kind: LeaveKind
  person: { id: number; name: string; code: string; detail: string | null }
  requestedBy: { id: number; name: string } | null
  startDate: string
  endDate: string
  halfDay: boolean
  days: number
  reason: string
  attachment: { id: number; name: string; size: number; downloadUrl: string } | null
  status: LeaveStatus
  decidedBy: { id: number; name: string } | null
  decidedAt: string | null
  decisionNote: string | null
  canCancel: boolean
  academicYear: string
  createdAt: string
  balance: LeaveBalanceImpact | null
}

export type LeaveCounts = Record<LeaveStatus | 'all', number>

export interface LeaveCalendarEntry {
  requestId: number
  kind: LeaveKind
  personName: string
  type: { id: number; name: string; colour: LeaveColour }
  status: LeaveStatus
  startDate: string
  endDate: string
  halfDay: boolean
}

export interface LeaveCalendarData {
  from: string
  to: string
  closedWeekdays: number[]
  entries: LeaveCalendarEntry[]
}

export const leaveStatusTone: Record<LeaveStatus, NonNullable<BadgeProps['tone']>> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
}

/** "1 day", "0.5 days", "2.5 days" ("0,5 jour" in French) — days are whole or half. */
export function formatDays(days: number): string {
  const text = new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: Number.isInteger(days) ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(days)
  return i18n.t('{{days}} days', { ns: 'leave', count: days, days: text })
}

export function formatRange(r: Pick<LeaveRequest, 'startDate' | 'endDate' | 'halfDay'>): string {
  if (r.startDate === r.endDate)
    return `${formatDate(r.startDate)}${r.halfDay ? ` (${i18n.t('half day', { ns: 'leave' })})` : ''}`
  return `${formatDate(r.startDate)} – ${formatDate(r.endDate)}`
}
